import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { ClickAwayListener, ListItemButton, type ListItemButtonProps, Tooltip, Typography } from "@mui/material";
import { Fragment, type ReactElement, useState } from "react";
import { MenuPopper } from "./menuPopper";
import { CascadingMenuStyled, menuClasses } from "./styles";

export interface ICascadingMenuProps {
  items: {
    label: string;
    onConfirm: () => void;
    onCancel?: () => void;
    component?: (onConfirm: () => void, onCancel?: () => void) => ReactElement;
    disabled?: boolean;
    tooltip?: string;
  }[];
  itemStyles?: ListItemButtonProps;
}

export const CascadingMenu = ({ items, itemStyles }: ICascadingMenuProps) => {
  const [CurrentComponent, setCurrentComponent] = useState<ReactElement | undefined>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <ClickAwayListener
      onClickAway={() => {
        setAnchorEl(null);
      }}
    >
      <CascadingMenuStyled className={menuClasses.root}>
        {items.map((item, index) => (
          <Fragment key={`${item.label}-${index}`}>
            {item.component && (
              <Tooltip title={item.tooltip} disableHoverListener={!item.disabled}>
                <span>
                  <ListItemButton
                    className={menuClasses.item}
                    sx={{ ...itemStyles?.sx }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentComponent(
                        item.component?.(
                          () => {
                            item.onConfirm();
                            setAnchorEl(null);
                          },
                          () => {
                            setAnchorEl(null);
                            item.onCancel?.();
                          }
                        )
                      );
                      setAnchorEl(e.currentTarget);
                    }}
                    disabled={item.disabled}
                  >
                    {typeof item.label === "string" ? (
                      <Typography data-content={item.label} variant="body1" />
                    ) : (
                      item.label
                    )}
                    <NavigateNextIcon color="primary" fontSize="small" />
                  </ListItemButton>
                </span>
              </Tooltip>
            )}
            {!item.component && (
              <ListItemButton
                className={menuClasses.item}
                sx={{ ...itemStyles?.sx }}
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(null);
                  item.onConfirm();
                }}
                disabled={item.disabled}
              >
                <Typography variant="body1">{item.label}</Typography>
              </ListItemButton>
            )}
          </Fragment>
        ))}
        <MenuPopper anchor={anchorEl}>{CurrentComponent}</MenuPopper>
      </CascadingMenuStyled>
    </ClickAwayListener>
  );
};
